import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { faFileDownload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { StyledBody } from 'baseui/card';
import { Input } from 'baseui/input';
import { ListItem, ListItemLabel } from 'baseui/list';
import { FIELD_CONFIG } from 'configs/columns';
import { FilterContextProvider } from 'context/FilterContext';
import FilterInputs from 'features/core/filter/FilterInputs';
import { DateTime } from 'luxon';
import prettyBytes from 'pretty-bytes';
import { theme } from 'style/ORSNNTheme';
import { Checkbox, DownloadDocumentButton } from 'ui-kit';
import DealButton from 'ui-kit/DealButton/DealButton';
import { downloadElementAsPDF } from 'utils/pdfUtils';

import { useLazyQuery, useMutation } from '@apollo/client';

import { DealRole, LoanField, Filter } from '__generated__/globalTypes';

import { AcceptBid, AcceptBidVariables } from 'mutation/__generated__/AcceptBid';
import { SaveSellerBid, SaveSellerBidVariables } from 'mutation/__generated__/SaveSellerBid';
import { ACCEPT_BID } from 'mutation/acceptBid';
import { SAVE_SELLER_BID } from 'mutation/saveSellerBid';

import {
  CalculateBidPrice,
  CalculateBidPriceVariables,
} from 'query/__generated__/CalculateBidPrice';
import { CALCULATE_BID_PRICE } from 'query/calculateBidPrice';

import { ReadOnlyBidHistory } from '../../../../pages/marketplace/ActiveTrades/ReadOnlyBidHistory';
import {
  adaptBidCardBid,
  getOwnershipLabel,
} from '../../../../pages/marketplace/ActiveTrades/shared/bidUtils';
import { GET_DEAL_TIMELINE } from '../../fragments';
import BidSummarySection from '../BidSummarySection';
import BuyerBidSummary from '../BuyerBidSummary';
import CarveDiffTable from '../CarveDiffTable';
import {
  basisPointsToPercent,
  formatCalculatedPrice,
  List,
  percentToBasisPoints,
  StyledLabelBody,
  useDebouncedState,
} from '../utils';
import {
  BidCard_BidCard,
  BidCard_BidCard_bids_terms,
  BidCard_BidCard_bids_carve,
} from './__generated__/BidCard';
import SellerCounterOffer from './sellerCounterOffer';
import YieldMatrixAndCashflowTabs from './YieldMatrixAndCashflowTabs';
import { SellerBidReview } from '../../../../pages/marketplace/ActiveTrades/SellerBidReview';

const DEBOUNCE_DELAY = 1200;
const INPUT_PLACEHOLDER = '100.00';

// Utility function to convert Apollo Client cached carve filters to clean Filter objects
// This strips the __typename field that Apollo Client adds to cached objects
const convertCarveFiltersToCleanFilters = (
  carveFilters: (BidCard_BidCard_bids_carve & { __typename?: string })[],
): Omit<BidCard_BidCard_bids_carve, '__typename'>[] => {
  return carveFilters.map((filter) => {
    // Use object destructuring to omit __typename while preserving all other fields
    const { __typename, ...cleanFilter } = filter;
    return cleanFilter;
  });
};

const ListItemStyleProps = {
  overrides: {
    Root: {
      style: {
        backgroundColor: theme.color.cardBackground,
        maxHeight: '40px',
      },
    },
  },
};

function renderListCol(termsDocuments: BidCard_BidCard_bids_terms[]): JSX.Element[] {
  return termsDocuments.map((doc) => {
    const { name, documentSizeBytes, documentUrl } = doc;
    return (
      <ListItem key={name} {...ListItemStyleProps}>
        <ListItemLabel>
          <StyledLabelBody>
            {`${name} - ${prettyBytes(documentSizeBytes)}`}
            <a target="blank" href={documentUrl}>
              <FontAwesomeIcon icon={faFileDownload} />
            </a>
          </StyledLabelBody>
        </ListItemLabel>
      </ListItem>
    );
  });
}

function renderList(termsDocuments: BidCard_BidCard_bids_terms[]): JSX.Element[] {
  const halfListLength = Math.ceil(termsDocuments.length / 2);
  return [termsDocuments.slice(0, halfListLength), termsDocuments.slice(halfListLength)].map(
    (halfDocs, index) => <List key={halfDocs[0]?.name ?? index}>{renderListCol(halfDocs)}</List>,
  );
}

type Props = {
  dealId: string;
} & Omit<BidCard_BidCard, 'bid'>;

const BidCardBody = ({
  dealId,
  bids,
  role,
  can_edit_counteroffer,
  counteroffer_details_section: _counteroffer_details_section,
}: Props) => {
  // Derive the current/main bid from bids array (latest buyer bid)
  const currentBid = useMemo(() => {
    if (!bids || bids.length === 0) return null;
    // Find the latest buyer bid (not counteroffer)
    return bids.find((b) => b.bidder === DealRole.BUYER) || bids[0];
  }, [bids]);

  const [withCounteroffer, setWithCounteroffer] = useState<boolean>(false);
  const [savedCounteroffer, setSavedCounteroffer] = useState<boolean>(false);
  const [sellerBidPercent, dSellerBidPercent, setSellerBidPercent] = useDebouncedState<string>(
    '',
    DEBOUNCE_DELAY,
  );

  // Track user intent to prevent automatic state resets during polling updates
  const userOpenedCounteroffer = useRef<boolean>(false);

  // Bid selection state for yield chart integration
  const [selectedBidId, setSelectedBidId] = useState<string>('');

  const [calculateBidPrice, { data }] = useLazyQuery<CalculateBidPrice, CalculateBidPriceVariables>(
    CALCULATE_BID_PRICE,
  );

  const [acceptBid] = useMutation<AcceptBid, AcceptBidVariables>(ACCEPT_BID, {
    onError: (error) => {
      console.error('Bid acceptance failed:', error);
      // Show user-friendly error message
      if (error.message.includes('conditional request failed')) {
        alert(
          'Bid acceptance failed: The deal status may have changed. Please refresh and try again.',
        );
      } else {
        alert(`Bid acceptance failed: ${error.message}`);
      }
    },
  });
  const [saveSellerBid] = useMutation<SaveSellerBid, SaveSellerBidVariables>(SAVE_SELLER_BID, {
    onCompleted: () => {
      setSavedCounteroffer(true);
      // Clear counteroffer state after successful submission
      setWithCounteroffer(false);
    },
  });

  // Get the selected bid object for yield chart integration
  const selectedBid = useMemo(() => {
    if (bids && selectedBidId && currentBid) {
      const foundBid = bids.find((b) => b.bid_id === selectedBidId);
      // If selected bid no longer exists, reset to current bid and update selection
      if (!foundBid) {
        setSelectedBidId(currentBid.bid_id);
        return currentBid;
      }
      return foundBid;
    }
    return currentBid;
  }, [bids, selectedBidId, currentBid]);

  // Bid action handlers for yield chart integration
  const handleAccept = useCallback(() => {
    // Validate before attempting to accept
    if (!selectedBidId) {
      alert('Please select a bid to accept.');
      return;
    }

    setWithCounteroffer(false);
    // Reset user intent when accepting a bid
    userOpenedCounteroffer.current = false;
    acceptBid({
      variables: { input: { deal_id: dealId, bid_id: selectedBidId } },
      refetchQueries: [{ query: GET_DEAL_TIMELINE, variables: { id: dealId } }],
    });
  }, [dealId, selectedBidId, acceptBid]);

  // Enhanced handlers for specialized bid components
  const handleAcceptBid = useCallback(
    (bidId: string) => {
      acceptBid({
        variables: { input: { deal_id: dealId, bid_id: bidId } },
        refetchQueries: [{ query: GET_DEAL_TIMELINE, variables: { id: dealId } }],
      });
    },
    [dealId, acceptBid],
  );

  // Handle explicit counteroffer submission
  const handleSubmitCounteroffer = useCallback(() => {
    if (selectedBidId && dSellerBidPercent.length > 0) {
      const basisPoints = percentToBasisPoints(dSellerBidPercent);
      saveSellerBid({
        variables: {
          input: {
            deal_id: dealId,
            bid_id: selectedBidId,
            seller_bid_basis_points: basisPoints,
          },
        },
        refetchQueries: [{ query: GET_DEAL_TIMELINE, variables: { id: dealId } }],
      });
      setWithCounteroffer(false);
      // Reset user intent after successful submission
      userOpenedCounteroffer.current = false;
    }
  }, [dealId, selectedBidId, dSellerBidPercent, saveSellerBid]);

  // Bid selection handler for yield chart integration
  const handleSelectBid = useCallback(
    (bidId: string) => {
      console.log('BidCardBody: handleSelectBid called with bidId:', bidId, 'role:', role);
      setSelectedBidId(bidId);
    },
    [role],
  );

  // Initialize state based on current bid (only if no bid is currently selected)
  useEffect(() => {
    if (currentBid && !selectedBidId) {
      // Only set initial selection if user hasn't made a selection yet
      setSelectedBidId(currentBid.bid_id);
    }
  }, [currentBid, selectedBidId]);

  // Initialize counteroffer state separately from bid selection
  useEffect(() => {
    if (currentBid && bids) {
      // Check if there's a seller counteroffer by looking for seller bids in the array
      const sellerCounteroffer = bids?.find((b) => b.bidder === DealRole.SELLER);
      const hasSellerBid = sellerCounteroffer != null;
      setSavedCounteroffer(hasSellerBid);
      setSellerBidPercent(basisPointsToPercent(sellerCounteroffer?.bid_basis_points || null));
    }
  }, [currentBid, bids, setSellerBidPercent]);

  // Calculate bid price for preview when counteroffer changes (but don't auto-save)
  useEffect(() => {
    if (withCounteroffer && dSellerBidPercent.length > 0) {
      const basisPoints = percentToBasisPoints(dSellerBidPercent);

      // Strip __typename from carve filters to avoid GraphQL validation errors
      const carveFilters =
        selectedBid && 'carve' in selectedBid && selectedBid.carve
          ? convertCarveFiltersToCleanFilters(selectedBid.carve)
          : [];

      calculateBidPrice({
        variables: {
          input: {
            deal_id: dealId,
            bid_basis_points: basisPoints,
            // Pass carve filters directly for historical accuracy
            carve: carveFilters,
          },
        },
      });
    }
  }, [withCounteroffer, dSellerBidPercent, dealId, selectedBid]);

  // Update counteroffer state when selected bid changes
  useEffect(() => {
    if (selectedBid) {
      // Find the corresponding seller counteroffer for this buyer bid
      const correspondingSellerBid = bids?.find(
        (b) => b.bidder === DealRole.SELLER && b.prior_bid_id === selectedBid.bid_id,
      );
      const sellerBasisPoints = correspondingSellerBid?.bid_basis_points || null;
      setSavedCounteroffer(sellerBasisPoints != null);
      setSellerBidPercent(basisPointsToPercent(sellerBasisPoints));

      // IMPORTANT: Preserve user intent for counteroffer UI
      // Only close the counteroffer UI if the user hasn't explicitly opened it
      // This prevents polling updates from auto-closing the counteroffer section
      if (!userOpenedCounteroffer.current) {
        // User hasn't explicitly opened counteroffer, safe to update based on data
        setWithCounteroffer(false);
      }
      // If userOpenedCounteroffer.current is true, keep the UI open regardless of data changes
    }
  }, [selectedBid, bids, setSellerBidPercent]);

  // Check if bid has unsaved changes
  const isBidUnsaved = useMemo(
    () => !savedCounteroffer && dSellerBidPercent !== sellerBidPercent,
    [savedCounteroffer, dSellerBidPercent, sellerBidPercent],
  );

  // Early return if no bids - MUST be after all hooks
  if (!currentBid) {
    return (
      <StyledBody>
        <div className="text-gray-500">No bids available for this deal.</div>
      </StyledBody>
    );
  }

  // Use selectedBid for all data, fallback to currentBid
  const activeBid = selectedBid || currentBid;

  const hasCarve = activeBid.carve != null && activeBid.carve.length !== 0;
  const filtersWithIds = activeBid.carve?.map((filter) => ({
    ...filter,
    id: `${filter.field_name}-${filter.operator}`,
  }));

  const carveDetailsSection = hasCarve && (
    <>
      <span>CARVE CRITERIA SPECIFIED</span>
      <div className="pl-4">
        <FilterContextProvider filters={filtersWithIds}>
          {filtersWithIds != null && <FilterInputs dealId={dealId} canEdit={false} />}
        </FilterContextProvider>
      </div>
    </>
  );

  const contractsSection = activeBid.terms && (
    <ul className="m-0 flex-1 list-none p-0">
      <span>CONTRACTS</span>
      <div className="flex p-2">
        {hasCarve ? renderList(activeBid.terms) : renderListCol(activeBid.terms)}
      </div>
    </ul>
  );

  const {
    currentBalance,
    price: sellerBidPrice,
    rate: sellerRate,
  } = formatCalculatedPrice({
    currentBalanceCents: data?.calculateBidPrice.unpaid_balance,
    priceCents: data?.calculateBidPrice.price,
    rate_bps: data?.calculateBidPrice.rate_bps,
  });

  const counterofferSection = can_edit_counteroffer && (
    <>
      <Checkbox
        checked={withCounteroffer}
        onChange={() => {
          const newState = !withCounteroffer;
          setWithCounteroffer(newState);
          // Track user intent - they explicitly opened/closed the counteroffer
          userOpenedCounteroffer.current = newState;
        }}
        label="Provide counteroffer"
      />
      {withCounteroffer && (
        <div className="mt-4 flex flex-nowrap gap-x-3">
          <div className="flex w-[50%] flex-col gap-2">
            Define % of UPB
            <Input
              type="number"
              value={sellerBidPercent}
              disabled={!withCounteroffer}
              onChange={(event) => {
                setSavedCounteroffer(false);
                setSellerBidPercent(event.currentTarget.value);
              }}
              placeholder={INPUT_PLACEHOLDER}
              endEnhancer="%"
            />
          </div>
          <div className="flex w-[50%] flex-col gap-2">
            <BidSummarySection
              currentBalance={currentBalance}
              bidPrice={sellerBidPrice}
              pricePercent={sellerBidPercent}
              rate={sellerRate}
            />
          </div>
        </div>
      )}
    </>
  );

  const handleDownloadPDF = async () => {
    await downloadElementAsPDF('pdf-content', 'BidDetails', 10);
  };

  // Check selected bid status for contextual button states
  const selectedBidStatus = selectedBid && 'status' in selectedBid ? selectedBid.status : null;
  const isSelectedBidAccepted = selectedBidStatus === 'ACCEPTED';

  // Only allow seller actions when no bid has been accepted (deal still in BUYER_OFFER status)
  const hasBidBeenAccepted = bids?.some((bid) => bid.status === 'ACCEPTED');
  const canPerformSellerActions = can_edit_counteroffer && !hasBidBeenAccepted;

  const buttons = canPerformSellerActions && (
    <>
      {withCounteroffer ? (
        <>
          <DealButton
            onClick={handleSubmitCounteroffer}
            disabled={!selectedBidId || dSellerBidPercent.length === 0}
            variant="primary"
            label="submit"
          >
            Submit Counteroffer
          </DealButton>
          <DealButton
            onClick={() => handleAccept()}
            disabled={withCounteroffer || isSelectedBidAccepted}
            variant="primary"
            label="accept"
          >
            Accept Selected Bid
          </DealButton>
        </>
      ) : (
        <DealButton
          onClick={() => handleAccept()}
          disabled={isSelectedBidAccepted}
          variant="success"
          label="accept"
        >
          {isSelectedBidAccepted ? 'Already Accepted' : 'Accept Selected Bid'}
        </DealButton>
      )}
      {isSelectedBidAccepted && (
        <div className="mt-2 text-sm text-gray-500">
          Selected bid ({selectedBidId}) has been accepted.
        </div>
      )}
    </>
  );

  // Use bid_basis_points (not stipulations.bid_basis_points) to get correct counteroffer prices
  const buyerPrice = activeBid.bid_basis_points || 0;

  const buyerGwac = activeBid.carve_summary?.wa_coupon_bps;
  const buyerBidData = {
    price: basisPointsToPercent(buyerPrice) + '%',
    gwac: buyerGwac != null ? `${(buyerGwac / 100).toFixed(3)}%` : '-',
    upb: activeBid.price?.unpaid_balance
      ? FIELD_CONFIG[LoanField.current_balance_cents].display(activeBid.price.unpaid_balance)
      : '-',
    settleDate: activeBid.stipulations?.settlement_date_seconds
      ? DateTime.fromSeconds(activeBid.stipulations.settlement_date_seconds).toLocaleString(
          DateTime.DATE_SHORT,
        )
      : 'N/A',
    ownership: getOwnershipLabel(adaptBidCardBid(activeBid)),
  };

  // Find the specific seller counteroffer for the selected buyer bid
  const correspondingSellerBid = bids?.find(
    (b) =>
      b.bidder === DealRole.SELLER && b.prior_bid_id === (selectedBid?.bid_id || currentBid.bid_id),
  );

  const sellerCounterofferData = {
    price: correspondingSellerBid
      ? basisPointsToPercent(correspondingSellerBid.bid_basis_points || 0) + '%'
      : '-',
    gwac: correspondingSellerBid?.carve_summary?.wa_coupon_bps
      ? `${(correspondingSellerBid.carve_summary.wa_coupon_bps / 100).toFixed(3)}%`
      : '-',
    upb: correspondingSellerBid?.price?.unpaid_balance
      ? FIELD_CONFIG[LoanField.current_balance_cents].display(
          correspondingSellerBid.price.unpaid_balance,
        )
      : '-',
    settleDate: correspondingSellerBid?.stipulations?.settlement_date_seconds
      ? DateTime.fromSeconds(
          correspondingSellerBid.stipulations.settlement_date_seconds,
        ).toLocaleString(DateTime.DATE_SHORT)
      : 'N/A',
    ownership: correspondingSellerBid
      ? getOwnershipLabel(adaptBidCardBid(correspondingSellerBid))
      : '-',
  };

  // Counteroffer details section controls seller counteroffer display
  const showSellerCounterOffer = true;

  return (
    <StyledBody>
      <div className="flex flex-col gap-4">
        {/* Enhanced Multi-Bidding History */}
        {bids && bids.length > 0 && (
          <div className="flex flex-col gap-3">
            {/* Scrollable bid list with max height constraint */}
            <div className="max-h-[50vh] overflow-y-auto border border-gray-700 bg-gray-900">
              {role === DealRole.SELLER ? (
                <SellerBidReview
                  bids={bids.map(adaptBidCardBid)}
                  onAcceptBid={handleAcceptBid}
                  onSelectBid={handleSelectBid}
                  selectedBidId={selectedBidId}
                  canManageBids={true}
                />
              ) : (
                <ReadOnlyBidHistory
                  bids={bids.map(adaptBidCardBid)}
                  userRole={role}
                  selectedBidId={selectedBidId}
                  onBidSelect={setSelectedBidId}
                />
              )}
            </div>
          </div>
        )}

        {/* Bid Offer Section Header with Download */}
        {bids && bids.length > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-lg font-medium">BID OFFER</span>
            <DownloadDocumentButton
              fileType="pdf"
              onClick={handleDownloadPDF}
              tooltipText="Download selected bid details as PDF"
            />
          </div>
        )}

        {/* Selected Bid Details Section */}
        <div
          className="mt-4 flex flex-nowrap gap-x-3 bg-background-canvas px-[20px]"
          id="pdf-content"
        >
          <div className="flex w-[50%] flex-col gap-4">
            {/* Buyer/Seller Bid Context */}
            {role === DealRole.BUYER ? (
              <div className="border border-blue-600 bg-blue-900 p-4">
                <h4 className="mb-3 text-lg font-medium text-blue-300">
                  {activeBid.carve_name} Bid
                </h4>

                {activeBid.carve && activeBid.carve.length > 0 && (
                  <div className="mb-3">
                    <p className="mb-2 text-sm text-gray-300">This bid was for loans matching:</p>
                    <div className="space-y-1">
                      {activeBid.carve.map((filter, index) => (
                        <div key={index} className="text-sm">
                          <span className="font-medium text-white">{filter.field_name}</span>
                          <span className="text-gray-400"> {filter.operator} </span>
                          <span className="text-blue-300">{filter.operand}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {correspondingSellerBid && (
                  <div className="border-t border-blue-600 pt-3">
                    <p className="mb-2 text-sm font-medium text-yellow-300">
                      Seller Counteroffer Received
                    </p>
                    <div className="flex justify-between text-sm">
                      <span>
                        Original Bid:{' '}
                        <span className="font-medium">{basisPointsToPercent(buyerPrice)}%</span>
                      </span>
                      <span>
                        Seller Counter:{' '}
                        <span className="font-medium text-yellow-300">
                          {basisPointsToPercent(correspondingSellerBid.bid_basis_points || 0)}%
                        </span>
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              carveDetailsSection
            )}

            <BuyerBidSummary bid={selectedBid || currentBid} title="Offer Details" />
            {!hasCarve ? contractsSection : <></>}
            {showSellerCounterOffer && role === DealRole.SELLER && (
              <SellerCounterOffer
                buyerBid={buyerBidData}
                sellerCounteroffer={sellerCounterofferData}
              />
            )}
          </div>
          <div className="flex w-[50%] flex-col gap-2">
            <CarveDiffTable
              dealId={dealId}
              filteredPool={selectedBid?.carve_summary || currentBid.carve_summary}
            />
          </div>
        </div>

        {hasCarve ? contractsSection : <></>}
        {counterofferSection}
        <div className="flex gap-4">{buttons}</div>

        {/* Historical view message when selected bid has been accepted */}
        {isSelectedBidAccepted && (
          <div className="mt-4 rounded-lg border border-green-600 bg-green-900/10 p-4">
            <div className="text-center">
              <h4 className="mb-2 text-lg font-medium text-green-300">Bid Accepted</h4>
              <p className="text-sm text-gray-300">
                This deal has moved to diligence. The information above is for historical reference
                only.
              </p>
            </div>
          </div>
        )}
      </div>
      {role === DealRole.SELLER && (
        <div className="mt-4">
          <div className="mb-2 text-sm text-gray-600">
            {selectedBidId !== currentBid.bid_id
              ? `Analyzing selected bid: ${selectedBidId}`
              : `Analyzing current bid: ${selectedBidId}`}
          </div>
          <YieldMatrixAndCashflowTabs
            dealId={dealId}
            bidId={selectedBidId}
            onPriceSelect={setSellerBidPercent}
            buyerPrice={buyerPrice}
          />
        </div>
      )}
    </StyledBody>
  );
};

export default BidCardBody;
export { default as BidCardFragments } from './fragments';
